"use client";

import { ArrowRight, Plus, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  question: "Is B.Y.E. Hygiene Mist safe for newborns and sensitive skin?",
  answer: <>
        <p className="mb-4">Yes, our formula is specifically crafted for the most delicate members of your family. It serves as the essential first step in your <Link to="#" className="text-accent-gold hover:underline">Before You Enter™</Link> ritual, ensuring a clean sanctuary for your baby.</p>
        <ul className="space-y-2 list-disc pl-5">
          <li>Plant-derived, non-toxic ingredients safe for frequent use.</li>
          <li>Free from harsh chemicals, synthetic fragrances, and alcohol.</li>
          <li>Dermatologically tested for peace of mind.</li>
        </ul>
        <p className="mt-4"><Link to="/AboutPage" className="text-accent-gold hover:underline font-medium">View our full safety certifications</Link>.</p>
      </>
}, {
  question: "How do I safely use the mist while traveling?",
  answer: <>
        <p className="mb-4">Our TSA-friendly bottle ensures your <Link to="/TravelHygienePage" className="text-accent-gold hover:underline">Travel Hygiene</Link> remains uncompromised. It provides a reliable barrier against environmental stressors in high-traffic areas.</p>
        <ul className="space-y-2 list-disc pl-5">
          <li>Mist hands and high-touch surfaces before you enter planes or hotels.</li>
          <li>Fast-drying formula with no sticky residue.</li>
          <li>Compact design fits perfectly in carry-ons and diaper bags.</li>
        </ul>
        <p className="mt-4"><Link to="/TravelHygienePage" className="text-accent-gold hover:underline font-medium">Explore our family travel guide</Link>.</p>
      </>
}, {
  question: "Can the mist be used in childcare centers?",
  answer: <>
        Absolutely. It is an essential part of the <Link to="/ChildcareCentersPage" className="text-accent-gold hover:underline">Childcare Centers</Link> safety protocol where parents and staff feel confident. Caregivers use it as a gentle ritual during drop-offs to ensure a clean environment. <Link to="/ChildcareCentersPage" className="text-accent-gold hover:underline font-medium">Read our childcare guidelines</Link>.
      </>
}, {
  question: "What makes this mist different from standard disinfectant sprays?",
  answer: <>
        <p className="mb-4">Unlike clinical sprays that rely on harsh odors and drying alcohols, our <Link to="/ProductPage" className="text-accent-gold hover:underline">Hygiene Mist</Link> offers a sophisticated, skin-loving alternative that doesn't compromise on effectiveness.</p>
        <ul className="space-y-2 list-disc pl-5">
          <li>Fine-mist technology provides superior coverage with less product.</li>
          <li>Infused with botanicals that hydrate rather than strip the skin.</li>
          <li>A subtle, calming scent that replaces the "hospital smell" of standard sprays.</li>
        </ul>
        <p className="mt-4"><Link to="/ProductPage" className="text-accent-gold hover:underline font-medium">See the science behind the mist</Link>.</p>
      </>
}, {
  question: "Is the packaging recyclable and eco-friendly?",
  answer: <>
        Caring for the earth is part of our promise; you’re making a sustainable choice with us. Our bottles are made from high-quality, recyclable materials because a <Link to="#" className="text-accent-gold hover:underline">gentle ritual</Link> should be kind to the planet. <Link to="/AboutPage" className="text-accent-gold hover:underline font-medium">See our sustainability story</Link>.
      </>
}, {
  question: "How long does one bottle of mist typically last?",
  answer: <>
        With daily use, a single bottle typically lasts four to six weeks, so you won’t run out unexpectedly. It integrates perfectly into your <Link to="#" className="text-accent-gold hover:underline">Before You Enter™</Link> routine. <Link to="/ShopPage" className="text-accent-gold hover:underline font-medium">Check subscription options</Link>.
      </>
}, {
  question: "Do you offer international shipping for the mist?",
  answer: <>
        Currently, we ship domestically to ensure every family receives their <Link to="#" className="text-accent-gold hover:underline">Hygiene Mist</Link> promptly, but we’re expanding soon. We are committed to reaching more families globally. <Link to="#" className="text-accent-gold hover:underline font-medium">Stay updated on shipping news</Link>.
      </>
}, {
  question: "Can I return my order if I’m not satisfied?",
  answer: <>
        Your satisfaction is our priority. If our <Link to="#" className="text-accent-gold hover:underline">gentle ritual</Link> doesn't feel right for your family, please reach out within 30 days for a stress-free return. <Link to="#" className="text-accent-gold hover:underline font-medium">Contact support for returns</Link>.
      </>
}];
export default function Faq() {
  return <section data-section-id="4087" id="common-questions" className="bg-cream-warm py-36">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <p className="font-default text-xs uppercase tracking-widest text-sage-deep/60 mb-8">
                Support
              </p>
              <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-sage-deep mb-8">
                Common Questions:{" "}
                <span className="block italic font-light text-accent-gold">
                  Your B.Y.E. Answers
                </span>
              </h2>
              <p className="font-default text-[17px] leading-relaxed text-sage-deep/80 max-w-md mb-8">
                Still curious about the B.Y.E. ritual? We're here to help your family feel at ease. Reach out to us anytime at{" "}
                <span className="text-sage-deep">hello@byehygiene.com</span>.
              </p>
              <div className="flex flex-col items-start gap-4">
                <Link to="/CheckoutPage" className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-widest text-sage-deep hover:text-accent-gold transition-colors duration-300 border-b border-sage-deep/30 hover:border-accent-gold pb-1">
                  Buy Now – Secure Checkout
                  <ArrowRight className="size-3" />
                </Link>
                <Link to="#" className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-widest text-sage-deep hover:text-accent-gold transition-colors duration-300 border-b border-sage-deep/30 hover:border-accent-gold pb-1">
                  Learn About Our Ritual
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>
          </div>

          
          <div className="lg:col-span-8 lg:col-start-6">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border-subtle last:border-b-0 group">
                  <AccordionTrigger className="hover:no-underline py-8 [&>svg]:hidden">
                    <span className="font-default text-xl md:text-[22px] leading-snug text-sage-deep text-left pr-6 group-hover:text-accent-gold transition-colors duration-300">
                      {faq.question}
                    </span>
                    <span className="relative flex items-center justify-center size-8 rounded-lg border border-border-subtle text-sage-deep shrink-0 transition-all duration-[400ms] group-hover:border-accent-gold group-hover:text-accent-gold">
                      <Plus className="size-4 absolute transition-all duration-[400ms] group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0" strokeWidth={1} />
                      <X className="size-4 absolute transition-all duration-[400ms] rotate-[-90deg] opacity-0 group-data-[state=open]:rotate-0 group-data-[state=open]:opacity-100" strokeWidth={1} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <div className="font-default text-[17px] text-sage-deep/80 max-w-[560px]" style={{
                  lineHeight: "1.65"
                }}>
                      {faq.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}